import { memo, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PrintIcon from '@mui/icons-material/Print';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SendIcon from '@mui/icons-material/Send';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  Alert,
  Box,
  Button,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';

interface WorkOrderStatusActionsProps {
  workOrder: WorkOrderDetail;
  t: TFunction;
  tc: TFunction;
  setWaitingForPartsPending: boolean;
  partsReceivedPending: boolean;
  disputePending: boolean;
  invoicingHoldPending: boolean;
  cancelWorkOrderPending: boolean;
  rejectQuotePending: boolean;
  onAcceptQuote: () => void;
  /** Send the quotation to the customer (#2651); absent hides the entry. */
  onSendQuote?: (() => void) | undefined;
  onRejectQuote: () => void | Promise<void>;
  onSetWaitingForParts: () => void | Promise<void>;
  onPartsReceived: () => void | Promise<void>;
  onRequestApproval: () => void;
  onOpenReasonDialog: (type: 'dispute' | 'invoicing_hold' | 'cancel') => void;
  onPrint: () => void;
  /** Additional-work offer is sent/partially_decided (#3203). */
  hasPendingOffer?: boolean;
  /** Permanent delete (#3343); absent (no permission) hides the entry. */
  onDeleteWorkOrder?: (() => void) | undefined;
  deleteWorkOrderPending?: boolean;
}

interface MenuAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

function WorkOrderStatusActionsInner({
  workOrder,
  t,
  tc,
  setWaitingForPartsPending,
  partsReceivedPending,
  disputePending,
  invoicingHoldPending,
  cancelWorkOrderPending,
  rejectQuotePending,
  onAcceptQuote,
  onSendQuote,
  onRejectQuote,
  onSetWaitingForParts,
  onPartsReceived,
  onRequestApproval,
  onOpenReasonDialog,
  onPrint,
  hasPendingOffer = false,
  onDeleteWorkOrder,
  deleteWorkOrderPending = false,
}: WorkOrderStatusActionsProps) {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const showWaitingForPartsButton =
    (workOrder.status === 'vehicle_arrived' || workOrder.status === 'in_progress') &&
    !workOrder.isWaitingForParts;

  const menuActions = useMemo<MenuAction[]>(() => {
    const actions: MenuAction[] = [];

    if (workOrder.status === 'quote') {
      if (onSendQuote) {
        actions.push({
          key: 'send-quote',
          label: t('workOrderActions.sendQuote'),
          icon: <ForwardToInboxIcon fontSize="small" />,
          onClick: onSendQuote,
        });
      }
      actions.push({
        key: 'accept-quote',
        label: t('workOrderActions.acceptQuote'),
        icon: <RequestQuoteIcon fontSize="small" />,
        onClick: onAcceptQuote,
      });
      actions.push({
        key: 'reject-quote',
        label: t('workOrderActions.rejectQuote'),
        icon: <ThumbDownIcon fontSize="small" />,
        onClick: () => { void onRejectQuote(); },
        disabled: rejectQuotePending,
      });
    }

    if (workOrder.isWaitingForParts) {
      actions.push({
        key: 'parts-received',
        label: t('workOrderFlags.partsReceived'),
        icon: <CheckCircleIcon fontSize="small" />,
        onClick: () => { void onPartsReceived(); },
        disabled: partsReceivedPending,
      });
    }

    if (workOrder.status === 'vehicle_arrived' || workOrder.status === 'in_progress') {
      actions.push({
        key: 'request-approval',
        label: t('workOrderActions.requestApproval'),
        icon: <SendIcon fontSize="small" />,
        onClick: onRequestApproval,
      });
    }

    if (workOrder.status === 'in_progress' && !workOrder.isDisputed) {
      actions.push({
        key: 'dispute',
        label: t('workOrderActions.dispute'),
        icon: <ReportProblemIcon fontSize="small" />,
        onClick: () => { onOpenReasonDialog('dispute'); },
        disabled: disputePending,
      });
    }

    if (workOrder.status === 'work_completed' && !workOrder.isInvoicingHold) {
      actions.push({
        key: 'invoicing-hold',
        label: t('workOrderActions.invoicingHold'),
        icon: <HourglassBottomIcon fontSize="small" />,
        onClick: () => { onOpenReasonDialog('invoicing_hold'); },
        disabled: invoicingHoldPending,
      });
    }

    if (!['completed', 'cancelled', 'quote_rejected'].includes(workOrder.status) && !workOrder.isDisputed) {
      actions.push({
        key: 'cancel-work-order',
        label: t('workOrderActions.cancelWorkOrder'),
        icon: <CancelIcon fontSize="small" />,
        onClick: () => { onOpenReasonDialog('cancel'); },
        disabled: cancelWorkOrderPending,
      });
    }

    // Work-order PDF stays here; invoice/receipt print lives on the Payments card (#1961).
    // A quotation prints as a quote (#2649), not as a work order.
    actions.push({
      key: 'print-pdf',
      label: workOrder.status === 'quote'
        ? t('workOrderActions.printQuote')
        : t('workOrderActions.printPdf'),
      icon: <PrintIcon fontSize="small" />,
      onClick: onPrint,
    });

    // An invoiced work order is accounting history; the server refuses it too.
    if (onDeleteWorkOrder && workOrder.status !== 'invoiced') {
      actions.push({
        key: 'delete-work-order',
        label: t('workOrderActions.deleteWorkOrder'),
        icon: <DeleteForeverIcon fontSize="small" color="error" />,
        onClick: onDeleteWorkOrder,
        disabled: deleteWorkOrderPending,
      });
    }

    return actions;
  }, [
    cancelWorkOrderPending,
    deleteWorkOrderPending,
    disputePending,
    invoicingHoldPending,
    onAcceptQuote,
    onDeleteWorkOrder,
    onOpenReasonDialog,
    onPartsReceived,
    onPrint,
    onRejectQuote,
    onSendQuote,
    onRequestApproval,
    partsReceivedPending,
    rejectQuotePending,
    t,
    workOrder,
  ]);

  if (!showWaitingForPartsButton && menuActions.length === 0 && !hasPendingOffer) {
    return null;
  }

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {hasPendingOffer && (
        <Alert
          severity="warning"
          sx={{ py: 0, px: 1.5 }}
          data-testid="pending-offer-invoice-warning"
        >
          {t('offers.pendingInvoiceWarning')}
        </Alert>
      )}
      {showWaitingForPartsButton && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<LocalShippingIcon />}
          onClick={() => { void onSetWaitingForParts(); }}
          disabled={setWaitingForPartsPending}
          sx={{
            whiteSpace: 'nowrap',
            color: 'warning.main',
            borderColor: 'warning.main',
            '&:hover': { borderColor: 'warning.main', bgcolor: alpha(theme.palette.warning.main, 0.08) },
          }}
        >
          {t('serviceActions.waitingParts')}
        </Button>
      )}

      {menuActions.length > 0 && (
        <>
          <Tooltip title={tc('moreOptions')}>
            <IconButton
              size="small"
              aria-label={tc('moreOptions')}
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              sx={{ minWidth: 36, minHeight: 36 }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          >
            {menuActions.map((action) => (
              <MenuItem
                key={action.key}
                disabled={action.disabled}
                onClick={() => {
                  handleMenuClose();
                  action.onClick();
                }}
              >
                <ListItemIcon>{action.icon}</ListItemIcon>
                {action.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
}

export const WorkOrderStatusActions = memo(
  WorkOrderStatusActionsInner,
  (prev, next) =>
    prev.t === next.t &&
    prev.tc === next.tc &&
    prev.setWaitingForPartsPending === next.setWaitingForPartsPending &&
    prev.partsReceivedPending === next.partsReceivedPending &&
    prev.disputePending === next.disputePending &&
    prev.invoicingHoldPending === next.invoicingHoldPending &&
    prev.cancelWorkOrderPending === next.cancelWorkOrderPending &&
    prev.rejectQuotePending === next.rejectQuotePending &&
    prev.workOrder.id === next.workOrder.id &&
    prev.workOrder.status === next.workOrder.status &&
    prev.workOrder.isWaitingForParts === next.workOrder.isWaitingForParts &&
    prev.workOrder.isDisputed === next.workOrder.isDisputed &&
    prev.workOrder.isInvoicingHold === next.workOrder.isInvoicingHold &&
    prev.hasPendingOffer === next.hasPendingOffer,
);
